import { ChangeDetectorRef, Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { ChatService } from "../../services/chat.service";
import { Observable, of, combineLatest } from "rxjs";
import { MatMenuTrigger } from "@angular/material/menu";
import { Chat } from "../../models/chat.model";
import { shareReplay, tap, map } from 'rxjs/operators';
import { ChatsListComponent } from "../chats-list/chats-list.component";
import { ChatComponent } from "../chat/chat.component";
import { NotificationsListComponent } from "../notifications-list/notifications-list.component";
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationService } from '../../services/notification.service';
import { Notification } from '../../models/notification.model';
import { PushyService } from '../../services/pushy.service';

@Component({
  selector: 'app-notifications-button',
  templateUrl: './notifications-button.component.html',
  styleUrls: ['./notifications-button.component.scss']
})
export class NotificationsButtonComponent implements OnInit {

  @ViewChild('menuTrigger') trigger!: MatMenuTrigger;
  @ViewChild('notificationsList') notificationsList?: NotificationsListComponent;
  @ViewChild('chatsList') chatsList?: ChatsListComponent;
  @ViewChild('chat') chat?: ChatComponent;

  isMenuOpen = false;
  chatLoading = false;
  selectedTab = 'Notifications';

  chatTabs = ['Chats', 'New'];
  selectedChatTab = 'Chats';

  notificationsCount$: Observable<number>;
  unseenChatsCount$!: Observable<number>;
  combinedCount$: Observable<number>;
  hasUnreadNotifications$: Observable<boolean>;

  chatList = this.chatService.chatList;
  chat$: Observable<Chat | null> = of(null);

  goBack = false;
  navigateUrl = '';

  constructor(
    private chatService: ChatService,
    private router: Router,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef,
    private notificationService: NotificationService,
    private pushyService: PushyService,
  ) {
    // Initialize notification observables
    this.notificationsCount$ = this.notificationService.unreadCount$;
    
    // Create combined count observable
    this.combinedCount$ = combineLatest([
      this.chatService.unseenChatsCount$,
      this.notificationsCount$
    ]).pipe(
      map(([chatCount, notificationCount]) => chatCount + notificationCount)
    );
    
    // Track if there are unread notifications for red dot indicator
    this.hasUnreadNotifications$ = this.notificationsCount$.pipe(
      map(count => count > 0)
    );
  }

  ngOnInit() {
    this.unseenChatsCount$ = this.chatService.unseenChatsCount$;
    
    // Check for pending native notification navigation
    //this.checkPendingNativeNavigation();
  }

  private checkPendingNativeNavigation(): void {
    const pendingNavigation = this.pushyService.checkPendingNativeNavigation();
    if (pendingNavigation) {
      // Handle the complex navigation that was deferred from the native notification
      setTimeout(() => {
        this.notificationService.navigateFromNotification(pendingNavigation);
      }, 500); // Small delay to ensure component is fully initialized
    }
  }

  @HostListener('window:popstate')
  async onBrowserBackBtnClose() {
    if (this.navigateUrl) {
      setTimeout(async () => {
        const url = this.navigateUrl;
        this.navigateUrl = '';
        await this.router.navigateByUrl(url);
      });
    } else if (this.isMenuOpen) {
      this.goBack = false;
      this.trigger.closeMenu();
    }
  }

  menuOpenChange(open: boolean) {
    this.isMenuOpen = open;
    if (open) {
      this.goBack = true;
      window.history.pushState(null, '', window.location.href);
    } else if (this.goBack) {
      this.goBack = false;
      window.history.back();
    }

    if(this.chatsList)
      this.chatsList.menuStateChange(open, this.selectedTab === 'Chat' && this.selectedChatTab === 'Chats');
    if(this.chat)
      this.chat.menuStateChange(open, this.selectedTab === 'Chat' && this.selectedChatTab !== 'Chats');
  }

  async onNavigate(url: string) {
    const currentUrl = this.route.snapshot.url.join('/');
    if (currentUrl !== url)
      this.navigateUrl = url;
    this.trigger.closeMenu();
  }

  onChatTabSelected(tab: string) {
    this.selectedChatTab = tab;
    if(tab === 'Chats') {
      this.chat$ = of(null);
      this.chatTabs = ['Chats', 'New'];
    }
    this.cd.detectChanges();
  }

  openChat(id: number) {
    this.chatLoading = true;
    this.chat$ = this.chatService.chatObservable(id).pipe(
      tap(() => this.chatLoading = false),
      shareReplay(1)
    );
    this.chatTabs = ['Chats', 'Current'];
    this.onChatTabSelected('Current');
  }

  onMenuClick(event: Event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.notificationsList?.onMenuClick(event);
    this.chatsList?.onMenuClick(event);
    this.chat?.onMenuClick(event);
  }

  // Notification event handlers
  async onNotificationClick(notification: Notification) {
    if (!notification.actionUrl) {
      return;
    }

    // Prevent history.back() when menu closes after navigation
    this.goBack = false;
    
    // Use the shared navigation service for consistent behavior
    await this.notificationService.navigateFromNotification(notification.actionUrl, notification.data);

    this.trigger.closeMenu();
  }

  async onNotificationDelete(notificationId: number): Promise<void> {
    try {
      await this.notificationService.deleteNotification(notificationId);
      // Badge count will automatically update via observables
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  }

  async onMarkAllNotificationsAsRead(): Promise<void> {
    try {
      await this.notificationService.markAllAsRead();
      // Badge count will automatically update via observables
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }

}
